import type { RefObject } from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { clamp } from "lodash-es";

import {
  MAX_STRING_CELL_HEIGHT,
  MIN_STRING_CELL_HEIGHT,
} from "src/cases/caseView/blocks/cellTypes";

type UseStringCellHeightOptions = {
  /** The editor's draft text — every change re-measures. */
  localValue: string;
  /** The user's explicit height: the stored value at open, updated by
   *  drags. Typing never reports below it; a drag pins the box to it. */
  floorHeight?: number;
  onHeightChange?: (height: number) => void;
};

type UseStringCellHeightResult = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  /** The expanded window's cell-width text mirror. */
  mirrorRef: RefObject<HTMLDivElement | null>;
  hasScrolledContent: boolean;
  handleScroll: () => void;
  /** Call from onChange — from then on measured heights are stored. */
  markTyped: () => void;
  /** Call on Escape — the close fit measures the original text. */
  markCancelled: () => void;
};

/**
 * Sizes a string cell's editor. Opening fits the content without storing
 * anything; typing stores every measured height; a drag pins the box
 * exactly; closing stores min(content, floor) — trimming, never growing.
 */
export const useStringCellHeight = ({
  localValue,
  floorHeight,
  onHeightChange,
}: UseStringCellHeightOptions): UseStringCellHeightResult => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const [hasScrolledContent, setHasScrolledContent] = useState(false);

  const hasTypedRef = useRef(false);
  const cancelledRef = useRef(false);
  const initialValueRef = useRef(localValue);

  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;
  const floorHeightRef = useRef(floorHeight);
  floorHeightRef.current = floorHeight;

  // "fit" grows the box with the content, never below `floor`; "exact"
  // pins it to `floor` and lets content scroll. The scrollbar is hidden
  // while measuring — it reserves width and adds a wrapped line.
  const measureTextarea = useCallback(
    (floor: number, mode: "fit" | "exact" = "fit"): number | null => {
      const ta = textareaRef.current;
      if (!ta) return null;
      ta.style.overflowY = "hidden";
      ta.style.height = "0";
      const contentHeight = ta.scrollHeight;
      const fitHeight = clamp(
        contentHeight,
        MIN_STRING_CELL_HEIGHT,
        MAX_STRING_CELL_HEIGHT,
      );
      const height =
        mode === "exact" && floor > 0 ? floor : Math.max(fitHeight, floor);
      ta.style.height = `${height}px`;
      ta.style.overflowY = contentHeight > height ? "auto" : "hidden";
      setHasScrolledContent(ta.scrollTop > 0);
      return height;
    },
    [],
  );

  // The expanded window edits at its own width, so its height comes from
  // the window trigger — a natural-height, cell-width mirror of the text.
  const measureMirror = useCallback((floor: number): number | null => {
    const mirror = mirrorRef.current;
    if (!mirror) return null;
    const fitHeight = clamp(
      mirror.offsetHeight,
      MIN_STRING_CELL_HEIGHT,
      MAX_STRING_CELL_HEIGHT,
    );
    return Math.max(fitHeight, floor);
  }, []);

  // Size on open and on every change; store only once the user typed.
  // A floorHeight change is a drag — pin to it so shrinking works.
  const prevFloorHeightRef = useRef(floorHeight);
  useLayoutEffect(() => {
    const mode = prevFloorHeightRef.current === floorHeight ? "fit" : "exact";
    prevFloorHeightRef.current = floorHeight;
    const floor = floorHeight ?? 0;
    const height = measureTextarea(floor, mode) ?? measureMirror(floor);
    if (height !== null && hasTypedRef.current) {
      onHeightChangeRef.current?.(height);
    }
  }, [localValue, floorHeight, measureTextarea, measureMirror]);

  // On close, trim to min(content, floor) — skipped for untouched cells,
  // and Escape measures the original text. The commit runs in a microtask:
  // StrictMode's dev remount (which also runs this cleanup) cancels it
  // synchronously, while it still lands before any follow-up click
  // handler (e.g. Cancel's height restore).
  const pendingFitRef = useRef<{ height: number } | null>(null);
  useLayoutEffect(() => {
    pendingFitRef.current = null;
    return () => {
      if (!hasTypedRef.current && floorHeightRef.current === undefined) return;
      const ta = textareaRef.current;
      if (ta && cancelledRef.current) {
        ta.value = initialValueRef.current;
      }
      const fitHeight = measureTextarea(0) ?? measureMirror(0);
      if (fitHeight === null) return;
      const floor = floorHeightRef.current;
      const height =
        floor === undefined ? fitHeight : Math.min(fitHeight, floor);
      const pendingFit = { height };
      pendingFitRef.current = pendingFit;
      queueMicrotask(() => {
        if (pendingFitRef.current !== pendingFit) return;
        pendingFitRef.current = null;
        onHeightChangeRef.current?.(height);
      });
    };
  }, [measureTextarea, measureMirror]);

  const handleScroll = useCallback(() => {
    const ta = textareaRef.current;
    if (ta) {
      setHasScrolledContent(ta.scrollTop > 0);
    }
  }, []);

  const markTyped = useCallback(() => {
    hasTypedRef.current = true;
  }, []);

  const markCancelled = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  return {
    textareaRef,
    mirrorRef,
    hasScrolledContent,
    handleScroll,
    markTyped,
    markCancelled,
  };
};
